import { useEffect, useRef } from "react";

/**
 * Lightweight canvas particle field that gently follows the cursor.
 * Neon-yellow squares (on-brand) with connecting lines when nearby.
 * Skips on touch-only / small screens for performance.
 */
export default function MouseParticles({ count = 1600, color = null }) {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const particlesRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    // Internal gating so the component can be mounted anywhere safely
    const okSize = window.matchMedia("(min-width: 768px)").matches;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!okSize || reducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Read accent color live from CSS variable so theme changes apply on next mount.
    const themeAccent =
      color ||
      (typeof window !== "undefined" &&
        getComputedStyle(document.documentElement)
          .getPropertyValue("--theme-accent-primary")
          .trim()) ||
      "#FFFFFF";

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const setSize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();

    // Seed particles
    const w = window.innerWidth;
    const h = window.innerHeight;
    particlesRef.current = Array.from({ length: count }, () => {
      const roll = Math.random();
      const type = roll < 0.88 ? "dust" : roll < 0.98 ? "node" : "packet";
      return {
        type,
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: type === "dust" ? 0.8 + Math.random() * 0.6 : type === "node" ? 1.6 : 2.6,
        phase: Math.random() * Math.PI * 2,
        twinkleSpeed: 0.005 + Math.random() * 0.03,
        life: 0,
        maxLife: 400 + Math.floor(Math.random() * 1200),
      };
    });

    const onMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
      mouseRef.current.x = -9999;
      mouseRef.current.y = -9999;
    };
    const onResize = () => setSize();

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", onResize);

    const ATTRACT_RADIUS = 260;
    const CONNECT_RADIUS = 110;

    const tick = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      ctx.clearRect(0, 0, W, H);

      const ps = particlesRef.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const mActive = mouseRef.current.active;

      // Update + draw particles
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];

        if (mActive) {
          const dx = mx - p.x;
          const dy = my - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < ATTRACT_RADIUS && dist > 0.1) {
            const force = (1 - dist / ATTRACT_RADIUS) * 0.06;
            p.vx += (dx / dist) * force;
            p.vy += (dy / dist) * force;
          }
        }

        // Damping + drift
        p.vx *= 0.96;
        p.vy *= 0.96;
        p.x += p.vx;
        p.y += p.vy;
        p.phase += p.twinkleSpeed;

        // Wrap edges
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        if (p.y < -10) p.y = H + 10;
        if (p.y > H + 10) p.y = -10;

        // Random glitch jitter
        const jitter = Math.random() < 0.02 ? 2 : 0;

        const baseAlpha = p.type === "dust" ? 0.3 : p.type === "node" ? 0.7 : 0.9;
        ctx.fillStyle = p.type === "packet" ? "#ffffff" : themeAccent;
        ctx.globalAlpha = baseAlpha + Math.sin(p.phase) * 0.15;
        ctx.fillRect(p.x - p.size / 2 + jitter, p.y - p.size / 2, p.size, p.size);

        // Packet streak
        if (p.type === "packet") {
          const speed = Math.hypot(p.vx, p.vy);
          if (speed > 0.4) {
            ctx.globalAlpha = 0.25;
            ctx.strokeStyle = themeAccent;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x - p.vx * 8, p.y - p.vy * 8);
            ctx.stroke();
          }
        }
      }

      // Connections — only between nodes + packets (dust is just twinkling stars)
      ctx.lineWidth = 1;
      const connectables = [];
      for (let i = 0; i < ps.length; i++) {
        if (ps[i].type !== "dust") connectables.push(ps[i]);
      }
      for (let i = 0; i < connectables.length; i++) {
        for (let j = i + 1; j < connectables.length; j++) {
          const a = connectables[i];
          const b = connectables[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < CONNECT_RADIUS * CONNECT_RADIUS) {
            const d = Math.sqrt(d2);
            const alpha = (1 - d / CONNECT_RADIUS) * 0.18;
            ctx.strokeStyle = themeAccent;
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Cursor halo
      if (mActive) {
        const pulse = 28 + Math.sin(performance.now() / 200) * 4;
        ctx.globalAlpha = 0.08;
        ctx.strokeStyle = themeAccent;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(mx, my, pulse, 0, Math.PI * 2);
        ctx.stroke();

        ctx.globalAlpha = 0.4;
        ctx.fillStyle = themeAccent;
        ctx.fillRect(mx - 2, my - 2, 4, 4);
      }

      ctx.globalAlpha = 1;
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", onResize);
    };
  }, [count, color]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="mouse-particles"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[1] opacity-60"
    />
  );
}
